import React, { useEffect, useState } from "react";
import { WalletTransactionCashOut } from "./WalletTransactionCashOut";
import { AnchorUser } from "./shared/AnchorUser";
import { FormateDate } from "./FormateDate";
import { useParams } from "react-router-dom";
import { getTotalTransactions, getTransactions } from "../requests/getTransactions";
import DataTable from "react-data-table-component";
import { useTranslation } from "react-i18next";

export const WalletsDetailsContent = () => {

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const { id } = useParams();
  const { t } = useTranslation();

  const fetchData = React.useCallback(async (page, perPage) => {
    setLoading(true);
    try {
      const offset = (page - 1) * perPage;
      setData(await getTransactions(perPage, offset, id) || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData(page, perPage);
  }, [fetchData, page, perPage]);

  useEffect(() => {
    (async () => {
      setTotalRows(await getTotalTransactions(id));
    })();
  }, [id]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage, newPage) => {
    setPerPage(newPerPage);
    setPage(newPage);
  };

  const conditionalRowStyles = [
    {
      when: row => row.ref_type === 4,
      style: {
        backgroundColor: '#fed7aa' // Tailwind color for bg-orange-200
      },
    },
    {
      when: row => row.description === 'Cash out',
      style: {
        backgroundColor: '#fecaca', // Tailwind color for bg-red-200
      },
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f3f4f6', // Tailwind color for bg-gray-100
        borderBottomColor: '#e5e7eb', // Tailwind color for border-gray-300
        minHeight: '56px', // Increase the height of header row
        padding: '2px', // Add padding to header row
      },
    },
    headCells: {
      style: {
        fontSize: '16px', // Increase the font size for header cells
        fontWeight: 'bold', // Make header cells bold
        justifyContent: 'center', // Align header text to the center
        padding: '2px', // Add padding to body cells
      },
    },
    cells: {
      style: {
        fontSize: '15px', // Increase the font size for body cells
        padding: '2px', // Add padding to body cells
      },
    },
  };

  const columns = [
    {
      name: t('user_info.date'), selector: row => row.created_at,
      cell: (row) => (
        <div className="text-xs">
          <FormateDate date={row.created_at} />
        </div>
      ),
      center: true,
      sortable: true,
      wrap: true,
      width: '12%'
    },
    {
      name: t('user_info.balance'), selector: (row) => row.balance,
      cell: (row) => (
        Number(row.balance).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      ),
      wrap: false,
      right: true,
    },
    {
      name: t('total'), selector: (row) => row.total,
      cell: (row) => (
        Number(row.total).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      ),
      right: true,
    },

    {
      name: t('user_info.referred'), selector: (row) => row.referred_user_id,
      cell: (row) => (
        row.referred_user_id && <AnchorUser user_id={row.referred_user_id} />
      ),
      center: true,
      sortable: true
    },
    {
      name: t('user_info.description'),
      selector: row => row.description,
      sortable: true,
      width: '40%'
    },
    { name: t('user_info.ref_type'), selector: row => row.ref_type, sortable: true, center: true },
    {
      name: t('user_info.address'), selector: row => row.created_at,
      cell: (row) => (
        <WalletTransactionCashOut wallet={row} />
      ),
      width: '20%'
    },
  ];

  return (
    <div style={{ paddingTop: '20px' }}>
      <DataTable
        highlightOnHover
        title={t('transactions')}
        columns={columns}
        data={data}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 25, 50, 75, 100, 250, 500, 1000]}
        conditionalRowStyles={conditionalRowStyles}
        customStyles={customStyles}
      />
    </div>
  );
};
